import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Car, PieChart, Brain, Bell, Menu, ChevronLeft,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { startPush, onPushOpen, onPushForeground } from '../../utils/push';

// The phone-app frame (iPhone / iPad / Android). Used instead of the web sidebar layout
// whenever the app runs inside the native shell. Bottom tab bar (Dashboard, Collection,
// Portfolio, Intelligence, Alerts), a compact top bar with a back arrow on inner screens and
// the Menu button on the right.

const TITLES = [
  ['/vehicles', 'Collection'],
  ['/classic-car-intelligence', 'Car Intelligence'],
  ['/dashboard', 'Dashboard'],
  ['/portfolio', 'Portfolio'],
  ['/alerts', 'Alerts'],
  ['/reports', 'Reports'],
  ['/expenses', 'Expenses'],
  ['/insurance', 'Insurance'],
  ['/road-tax', 'Road Tax'],
  ['/services', 'Services'],
  ['/mechanical', 'Mechanical'],
  ['/documents', 'Documents'],
  ['/forms', 'Forms & Checklists'],
  ['/team', 'Team'],
  ['/billing', 'Billing'],
  ['/settings', 'Settings'],
  ['/roadmap', 'Roadmap'],
  ['/menu', 'Menu'],
];

const titleFor = (path) => {
  if (path === '/vehicles/new') return 'New Vehicle';
  if (path.endsWith('/records')) return 'Records';
  if (path.startsWith('/vehicles/')) return 'Vehicle';
  const hit = TITLES.find(([p]) => path === p || path.startsWith(p + '/'));
  return hit ? hit[1] : '';
};

// Screens that are a tab root: no back arrow there.
const TAB_ROOTS = ['/', '/dashboard', '/vehicles', '/portfolio', '/classic-car-intelligence', '/alerts'];

const isDashboard = (p) => p === '/' || p === '/dashboard';
const isVehicles = (p) => p === '/vehicles' || p.startsWith('/vehicles/');
const isPortfolio = (p) => p === '/portfolio';
const isIntel = (p) => p === '/classic-car-intelligence';
const isAlerts = (p) => p === '/alerts';
// One car's page (not its records, edit or new-car screens): it has its own back button and header.
const isCarPage = (p) => /^\/vehicles\/[^/]+$/.test(p) && p !== '/vehicles/new';
// The black-and-gold screens: the Collection list and a car's page.
const isNoir = (p) => p === '/vehicles' || isCarPage(p);
// The five main screens have their own big greeting header, so no top bar.
const hasHero = (p) => p === '/' || p === '/dashboard' || p === '/portfolio' || p === '/vehicles' || p === '/classic-car-intelligence' || p === '/alerts' || isCarPage(p);

export const MobileShell = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [unpaid, setUnpaid] = useState(0);
  const scroller = useRef(null);
  const path = location.pathname;

  // Back to the top whenever the screen changes.
  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = 0;
  }, [path]);

  // Push notifications: refresh this phone's token, open the Alerts screen when a notification is
  // tapped, and show a small banner if one arrives while the app is open.
  useEffect(() => {
    startPush();
    const offOpen = onPushOpen((route) => navigate(route || '/alerts'));
    const offFg = onPushForeground((n) => {
      const text = [n?.title, n?.body].filter(Boolean).join(' — ');
      if (text) toast(text, { duration: 6000 });
    });
    return () => { offOpen(); offFg(); };
  }, [navigate]);

  // Badge on the Alerts tab: invoices still awaiting payment.
  useEffect(() => {
    let off = false;
    const load = async () => {
      try {
        const { count } = await supabase.from('expenses').select('id', { count: 'exact', head: true }).eq('payment_status', 'unpaid');
        if (!off) setUnpaid(count || 0);
      } catch { /* badge is optional */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { off = true; clearInterval(t); };
  }, []);

  const root = TAB_ROOTS.includes(path);
  const hero = hasHero(path);

  return (
    <div className={`m-shell${isNoir(path) ? ' is-noir' : ''}${isCarPage(path) ? ' is-car' : ''}`}>
      {!hero && (
        <header className="m-topbar">
          <div className="m-topbar-side">
            {root ? null : (
              <button className="m-iconbtn" aria-label="Back" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/vehicles'))}>
                <ChevronLeft size={24} />
              </button>
            )}
          </div>
          <div className="m-topbar-title">{titleFor(path)}</div>
          <div className="m-topbar-side m-topbar-right">
            {root && (
              <button className="m-iconbtn" aria-label="Menu" onClick={() => navigate('/menu')}>
                <Menu size={22} />
              </button>
            )}
          </div>
        </header>
      )}

      <main className="m-content" ref={scroller}>
        <div key={path} className="m-page-enter">
          {hero && !isNoir(path) && (
            <button className="m-menu-float" aria-label="Menu" onClick={() => navigate('/menu')}>
              <Menu size={20} />
            </button>
          )}
          {children}
        </div>
      </main>

      <nav className="m-tabbar" aria-label="Main">
        <NavLink to="/dashboard" className={() => `m-tab ${isDashboard(path) ? 'is-active' : ''}`}>
          <LayoutDashboard size={22} /><span>Dashboard</span>
        </NavLink>
        <NavLink to="/vehicles" className={() => `m-tab ${isVehicles(path) ? 'is-active' : ''}`}>
          <Car size={22} /><span>Collection</span>
        </NavLink>
        <NavLink to="/portfolio" className={() => `m-tab ${isPortfolio(path) ? 'is-active' : ''}`}>
          <PieChart size={22} /><span>Portfolio</span>
        </NavLink>
        <NavLink to="/classic-car-intelligence" className={() => `m-tab ${isIntel(path) ? 'is-active' : ''}`}>
          <Brain size={22} /><span>Intelligence</span>
        </NavLink>
        <NavLink to="/alerts" className={() => `m-tab ${isAlerts(path) ? 'is-active' : ''}`}>
          <span className="m-tab-iconwrap">
            <Bell size={22} />
            {unpaid > 0 && <span className="m-badge">{unpaid > 9 ? '9+' : unpaid}</span>}
          </span>
          <span>Alerts</span>
        </NavLink>
      </nav>
    </div>
  );
};
